import React, { useState, useRef, useEffect } from 'react';
import { Modal, View, Text, TouchableOpacity, ScrollView, StyleSheet, PanResponder, Platform, useWindowDimensions } from 'react-native';
import Ionicons from '@expo/vector-icons/Ionicons';
import { useTheme } from '../../context/ThemeContext';
import { fontWeights } from '../../styles/typographyTokens';
import { isMobileWebLayout } from '../../widget/widgetPosition';
import { webWheelStyles, WHEEL_SETTLE_MS, isCoarsePointerWeb, useWheelTouchLock } from './webWheel';
import { useTouchWheel } from './touchWheel';

import { ACCENT_TEXT, ON_ACCENT_TEXT } from '../../styles/accentText';
/**
 * Brand-styled date & time pickers built entirely from RN primitives — no
 * native module, so they run in the dev build without a rebuild and stay on
 * theme (light/dark). Web keeps its own `<input type="date/time">`; these are
 * only rendered on the native branch of the caller.
 *
 *   value  — date: 'YYYY-MM-DD', time: 'HH:MM' (24h).
 *   onChange fires with the same string format.
 */

const ACCENT = '#E98245';

const RU_MONTHS_NOM = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь',
];
const RU_MONTHS_GEN = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];
const RU_WD = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const pad = (n: number) => String(n).padStart(2, '0');

const parseISO = (v: string): { y: number; mo: number; d: number } | null => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(v || '');
  return m ? { y: +m[1], mo: +m[2] - 1, d: +m[3] } : null;
};

const parseHM = (v: string): { h: number; m: number } => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(v || '');
  return m ? { h: Math.min(23, +m[1]), m: Math.min(59, +m[2]) } : { h: 9, m: 0 };
};

const midnight = (y: number, mo: number, d: number) => new Date(y, mo, d).setHours(0, 0, 0, 0);

interface DateProps {
  value: string;
  onChange: (next: string) => void;
  /** Earliest selectable date, 'YYYY-MM-DD'. Earlier days are disabled. */
  minDate?: string;
  disabled?: boolean;
  testID?: string;
  accessibilityLabel?: string;
}

export const DatePickerField: React.FC<DateProps> = ({ value, onChange, minDate, disabled, testID, accessibilityLabel }) => {
  const { colors: tc, isDark } = useTheme();
  const [open, setOpen] = useState(false);

  const parsed = parseISO(value);
  const today = new Date();
  const [viewY, setViewY] = useState(parsed?.y ?? today.getFullYear());
  const [viewM, setViewM] = useState(parsed?.mo ?? today.getMonth());

  const min = minDate ? parseISO(minDate) : null;
  const label = parsed
    ? `${parsed.d} ${RU_MONTHS_GEN[parsed.mo]} ${parsed.y}`
    : 'Выберите дату';

  const openSheet = () => {
    // Re-centre the calendar on the current value each time it opens.
    setViewY(parsed?.y ?? today.getFullYear());
    setViewM(parsed?.mo ?? today.getMonth());
    setOpen(true);
  };

  const shiftMonth = (delta: number) => {
    const next = new Date(viewY, viewM + delta, 1);
    setViewY(next.getFullYear());
    setViewM(next.getMonth());
  };

  // Swipe to change month (owner-asked): left → next, right → previous. The
  // responder claims the gesture only on a clearly HORIZONTAL move, so day
  // taps and the backdrop press keep working. shiftMonth closes over state,
  // so route the (once-created) responder through a ref to the latest one.
  const shiftRef = useRef(shiftMonth);
  shiftRef.current = shiftMonth;
  const swipe = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_e, g) =>
        Math.abs(g.dx) > 18 && Math.abs(g.dx) > Math.abs(g.dy) * 1.5,
      onPanResponderRelease: (_e, g) => {
        if (g.dx <= -40) shiftRef.current(1);
        else if (g.dx >= 40) shiftRef.current(-1);
      },
    }),
  ).current;

  // Leading blanks (Mon-based) + day numbers for the viewed month.
  const firstWeekday = (new Date(viewY, viewM, 1).getDay() + 6) % 7;
  const daysInMonth = new Date(viewY, viewM + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array(firstWeekday).fill(null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];

  const isDisabledDay = (d: number) =>
    !!min && midnight(viewY, viewM, d) < midnight(min.y, min.mo, min.d);
  const isSelected = (d: number) =>
    !!parsed && parsed.y === viewY && parsed.mo === viewM && parsed.d === d;

  const pick = (d: number) => {
    if (isDisabledDay(d)) return;
    onChange(`${viewY}-${pad(viewM + 1)}-${pad(d)}`);
    setOpen(false);
  };

  return (
    <>
      <TouchableOpacity
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? 'Дата'}
        activeOpacity={0.8}
        onPress={disabled ? undefined : openSheet}
        style={[styles.field, fieldTheme(isDark), disabled && styles.fieldDisabled]}
      >
        <Text style={[styles.fieldText, { color: parsed ? tc.textPrimary : tc.textMuted }]} numberOfLines={1} accessible={false}>
          {label}
        </Text>
        <Ionicons name="calendar-outline" size={18} color={ACCENT_TEXT} accessible={false} />
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={() => setOpen(false)}>
          <TouchableOpacity
            testID={testID ? `${testID}-sheet` : undefined}
            activeOpacity={1}
            style={[styles.sheet, { backgroundColor: isDark ? '#162130' : '#FFFFFF' }]}
          >
            {/* Month header */}
            <View style={styles.calHead}>
              <TouchableOpacity hitSlop={10} onPress={() => shiftMonth(-1)} style={styles.calNav}>
                <Ionicons name="chevron-back" size={22} color={tc.textPrimary} />
              </TouchableOpacity>
              <Text style={[styles.calTitle, { color: tc.textPrimary }]}>
                {RU_MONTHS_NOM[viewM]} {viewY}
              </Text>
              <TouchableOpacity hitSlop={10} onPress={() => shiftMonth(1)} style={styles.calNav}>
                <Ionicons name="chevron-forward" size={22} color={tc.textPrimary} />
              </TouchableOpacity>
            </View>

            {/* Weekday row + day grid — one swipe surface for month paging */}
            <View {...swipe.panHandlers}>
            <View style={styles.weekRow}>
              {RU_WD.map((w) => (
                <Text key={w} style={[styles.weekDay, { color: tc.textMuted }]}>{w}</Text>
              ))}
            </View>

            {/* Day grid */}
            <View style={styles.grid}>
              {cells.map((d, i) => {
                if (d === null) return <View key={`b${i}`} style={styles.cell} />;
                const off = isDisabledDay(d);
                const sel = isSelected(d);
                return (
                  <TouchableOpacity
                    key={d}
                    style={styles.cell}
                    activeOpacity={0.7}
                    disabled={off}
                    onPress={() => pick(d)}
                  >
                    <View style={[styles.dayInner, sel && { backgroundColor: ACCENT }]}>
                      <Text
                        style={{
                          color: sel ? ON_ACCENT_TEXT : off ? tc.textMuted : tc.textPrimary,
                          opacity: off ? 0.4 : 1,
                          fontSize: 15,
                          fontWeight: sel ? '800' : '600',
                        }}
                      >
                        {d}
                      </Text>
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
            </View>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

interface TimeProps {
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
  testID?: string;
  accessibilityLabel?: string;
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 12 }, (_, i) => i * 5); // 5-minute steps

// Wheel geometry — a fixed row pitch lets us scroll the selected value to the
// column's centre on open (tester: «должно открываться соизмеримо текущему
// времени», not always at 00).
const ROW_H = 44;
const ROW_PITCH = ROW_H + 2; // + marginBottom
const COL_H = 200;           // matches styles.timeCols height
// Top/bottom padding sized so ANY row — including 00 and the last one — can
// physically reach the centre focus band (was 6px, which pinned the edge
// values out of focus: «больше не крутится, не попадает на фокус»).
const WHEEL_PAD = (COL_H - ROW_H) / 2;
// With that padding, row i sits centred exactly at offset i * ROW_PITCH — the
// same grid snapToInterval snaps to, so wheel, snap and selection agree.
const centerOffset = (index: number) => index * ROW_PITCH;

// Hoisted to module level — defining this inside TimePickerField gave it a new
// component identity on every parent render (each h/m change), so React
// remounted both ScrollViews and reset their scroll position mid-gesture.
const WheelColumn = ({ items, sel, onSelect, unit, scrollRef, textPrimary, textMuted, webWheel }: {
  items: number[];
  sel: number;
  onSelect: (n: number) => void;
  unit: string;
  scrollRef: React.RefObject<ScrollView | null>;
  textPrimary: string;
  textMuted: string;
  /** CORE-1724 — phone-width web: browser snapping + pick on settle (see webWheel.ts). */
  webWheel: boolean;
}) => {
  // Auto-select whatever lands in the focus band — the user then only
  // presses «Готово» (no extra tap on the number needed).
  const selectAt = (y: number) => {
    const idx = Math.min(items.length - 1, Math.max(0, Math.round(y / ROW_PITCH)));
    onSelect(items[idx]);
  };
  // Web has no momentum-end event: the wheel has stopped once scrolling goes quiet.
  const settleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => { if (settleTimer.current) clearTimeout(settleTimer.current); }, []);
  const onWebScroll = (e: { nativeEvent: { contentOffset: { y: number } } }) => {
    const y = e.nativeEvent.contentOffset.y;
    if (settleTimer.current) clearTimeout(settleTimer.current);
    settleTimer.current = setTimeout(() => selectAt(y), WHEEL_SETTLE_MS);
  };
  // CORE-1724 (QA 22.10, iOS) — a finger drives the column by our code, not native scroll.
  useTouchWheel(scrollRef, webWheel, { pitch: ROW_PITCH, count: items.length, onSettle: selectAt });

  return (
    <ScrollView
      ref={scrollRef}
      style={[styles.col, webWheel && webWheelStyles.col]}
      contentContainerStyle={{ paddingVertical: WHEEL_PAD }}
      showsVerticalScrollIndicator={false}
      snapToInterval={ROW_PITCH}
      decelerationRate="fast"
      onMomentumScrollEnd={(e) => selectAt(e.nativeEvent.contentOffset.y)}
      onScroll={webWheel ? onWebScroll : undefined}
      scrollEventThrottle={webWheel ? 16 : undefined}
    >
      {items.map((n, i) => {
        const active = n === sel;
        return (
          <TouchableOpacity
            key={n}
            activeOpacity={0.7}
            onPress={() => {
              onSelect(n);
              scrollRef.current?.scrollTo({ y: centerOffset(i), animated: true });
            }}
            style={[styles.colRow, active && { backgroundColor: ACCENT }, webWheel && webWheelStyles.row]}
          >
            <Text style={{ color: active ? ON_ACCENT_TEXT : textPrimary, fontSize: 18, fontWeight: active ? '800' : '600' }}>
              {pad(n)}
              <Text style={{ fontSize: 11, color: active ? ON_ACCENT_TEXT : textMuted }}> {unit}</Text>
            </Text>
          </TouchableOpacity>
        );
      })}
    </ScrollView>
  );
};

export const TimePickerField: React.FC<TimeProps> = ({ value, onChange, disabled, testID, accessibilityLabel }) => {
  const { colors: tc, isDark } = useTheme();
  const [open, setOpen] = useState(false);
  const init = parseHM(value);
  const [h, setH] = useState(init.h);
  const [m, setM] = useState(init.m);
  const hourRef = useRef<ScrollView>(null);
  const minRef = useRef<ScrollView>(null);
  // CORE-1724 — the web wheel fixes: phone-width web (owner's call, 16.10) and,
  // since QA 21.10, any finger screen on the web — an iPad in landscape is wider
  // than a phone but swipes just the same. Desktop with a mouse stays as it was.
  const { width } = useWindowDimensions();
  const webWheel = Platform.OS === 'web' && (isMobileWebLayout(true, width) || isCoarsePointerWeb());
  // CORE-1724 (QA 21.10, iOS) — the page behind the sheet is held by a JS touch
  // lock on the backdrop, not by `touch-action`; see webWheel.ts.
  const backdropRef = useRef<React.ComponentRef<typeof TouchableOpacity>>(null);
  useWheelTouchLock(backdropRef, webWheel && open);

  const openSheet = () => {
    const p = parseHM(value);
    // Snap to the nearest 5-min step so the column has a highlighted row.
    const snappedM = Math.round(p.m / 5) * 5 % 60;
    setH(p.h);
    setM(snappedM);
    setOpen(true);
    // Centre the current hour/minute so the wheel opens on «now», not at 00.
    setTimeout(() => {
      hourRef.current?.scrollTo({ y: centerOffset(p.h), animated: false });
      minRef.current?.scrollTo({ y: centerOffset(snappedM / 5), animated: false });
    }, 60);
  };

  const confirm = () => {
    onChange(`${pad(h)}:${pad(m)}`);
    setOpen(false);
  };

  return (
    <>
      <TouchableOpacity
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? 'Время'}
        activeOpacity={0.8}
        onPress={disabled ? undefined : openSheet}
        style={[styles.field, fieldTheme(isDark), disabled && styles.fieldDisabled]}
      >
        {/* CORE-583 — an empty value must READ empty. `parseHM('')` falls back to
            09:00 for the wheel's starting position, and this line used to print
            that fallback: the field showed «09:00» while the form still held no
            time. In the Live-session wizard that made «Продолжить» dead with
            every visible field apparently filled. Placeholder, like the date
            field above; the wheel still opens on 09:00. */}
        <Text
          style={[styles.fieldText, { color: value ? tc.textPrimary : tc.textMuted }]}
          numberOfLines={1}
          accessible={false}
        >
          {value ? `${pad(init.h)}:${pad(init.m)}` : 'Выберите время'}
        </Text>
        <Ionicons name="time-outline" size={18} color={ACCENT_TEXT} accessible={false} />
      </TouchableOpacity>

      <Modal visible={open} transparent animationType="fade" onRequestClose={() => setOpen(false)}>
        <TouchableOpacity ref={backdropRef} style={styles.backdrop} activeOpacity={1} onPress={() => setOpen(false)}>
          <TouchableOpacity
            testID={testID ? `${testID}-sheet` : undefined}
            activeOpacity={1}
            style={[styles.sheet, { backgroundColor: isDark ? '#162130' : '#FFFFFF' }]}
          >
            <Text style={[styles.calTitle, { color: tc.textPrimary, textAlign: 'center', marginBottom: 12 }]}>
              Время начала
            </Text>
            <View style={styles.timeCols}>
              {/* Centre focus band — highlights the middle of the wheel. */}
              <View pointerEvents="none" style={[styles.centerBand, { borderColor: isDark ? 'rgba(238,91,7,0.35)' : 'rgba(238,91,7,0.25)' }]} />
              <WheelColumn items={HOURS} sel={h} onSelect={setH} unit="ч" scrollRef={hourRef} textPrimary={tc.textPrimary} textMuted={tc.textMuted} webWheel={webWheel} />
              <WheelColumn items={MINUTES} sel={m} onSelect={setM} unit="м" scrollRef={minRef} textPrimary={tc.textPrimary} textMuted={tc.textMuted} webWheel={webWheel} />
            </View>
            <TouchableOpacity
              testID={testID ? `${testID}-done` : undefined}
              accessibilityRole="button"
              accessibilityLabel="Готово"
              activeOpacity={0.9}
              onPress={confirm}
              style={styles.doneBtn}
            >
              <Text style={styles.doneText} accessible={false}>Готово · {pad(h)}:{pad(m)}</Text>
            </TouchableOpacity>
          </TouchableOpacity>
        </TouchableOpacity>
      </Modal>
    </>
  );
};

const fieldTheme = (isDark: boolean) => ({
  borderColor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.12)',
  backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : '#FFFFFF',
});

const styles = StyleSheet.create({
  field: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 8,
    borderWidth: 1,
    borderRadius: 14,
    paddingVertical: 16,
    paddingHorizontal: 16,
  },
  fieldDisabled: { opacity: 0.55 },
  fieldText: { flex: 1, fontSize: 16, fontWeight: '600' },
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.55)',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  },
  sheet: {
    width: '100%',
    maxWidth: 360,
    borderRadius: 20,
    padding: 16,
  },
  calHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  calNav: { width: 40, height: 40, alignItems: 'center', justifyContent: 'center' },
  calTitle: { fontSize: 17, fontWeight: fontWeights.bold },
  weekRow: { flexDirection: 'row', marginBottom: 4 },
  weekDay: { flex: 1, textAlign: 'center', fontSize: 12, fontWeight: fontWeights.bold },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: { width: `${100 / 7}%`, aspectRatio: 1, alignItems: 'center', justifyContent: 'center', padding: 2 },
  dayInner: { width: '100%', height: '100%', borderRadius: 999, alignItems: 'center', justifyContent: 'center' },
  timeCols: { flexDirection: 'row', gap: 12, height: 200, position: 'relative' },
  centerBand: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: (200 - 44) / 2,
    height: 44,
    borderRadius: 12,
    borderWidth: 1.5,
  },
  col: { flex: 1 },
  colRow: { height: 44, borderRadius: 10, alignItems: 'center', justifyContent: 'center', marginBottom: 2 },
  doneBtn: {
    marginTop: 14,
    height: 48,
    borderRadius: 999,
    backgroundColor: ACCENT,
    alignItems: 'center',
    justifyContent: 'center',
  },
  doneText: { color: ON_ACCENT_TEXT, fontSize: 15, fontWeight: fontWeights.bold },
});
